import { EnhancedSearchBarILS } from "@components/SearchBar/EnhancedSearchBarILS";
import { invenioConfig } from "@config";
import { goTo } from "@history";
import { FrontSiteRoutes } from "@routes/urls";
import { searchHistoryApi } from "@api/search_history/search_history";
import { SearchHistoryModal } from "@modules/SearchControls/SearchHistory";
import React, { Component } from "react";
import ReactDOM from "react-dom";
import { Button, Icon } from "semantic-ui-react";
import { literatureApi } from "@api/literature";

const DEFAULT_SEARCH_HISTORY_PARAMS = {
  days: 30,
  search_term: "",
  sort_by: "date",
  sort_order: "desc",
  page: 1,
  per_page: 10,
  success_only: false,
};

const DEFAULT_SEARCH_STATS_PARAMS = {
  days: 30,
  include_popular: true,
  include_recent: true,
  popular_limit: 10,
  recent_limit: 20,
};

export class HomeSearchBar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isTrackingEnabled: false,
      isInitialized: false,
      showSearchHistory: false,
      searchHistory: [],
      searchStats: null,
      isLoadingHistory: false,
      isLoadingStats: false,
      error: null,
      suggestions: [],
      showSuggestions: false,
      currentQuery: "",
      isLoadingSuggestions: false,
      suggestionMetadata: new Map(),
    };

    // 🚀 Optimized debounce timing
    this.debouncedFetchSuggestions = this.debounce(this.fetchSuggestions, 150);
    this.searchContainerRef = React.createRef();
    this.suggestionContainerRef = React.createRef();
    this._isMounted = false;

    // 🚀 Cache for suggestions to avoid duplicate API calls
    this.suggestionCache = new Map();
    this.cacheTimeout = 30000; // 30 seconds cache

    // 🚀 Abort controller for cancelling in-flight requests
    this.currentRequestController = null;

    // Create AbortController for canceling requests
    this.abortController = new AbortController();
  }

  componentDidMount() {
    this._isMounted = true;
    this.initializeSearchHistoryApi();
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    // Set mounted flag to false
    this._isMounted = false;
    document.removeEventListener("mousedown", this.handleClickOutside);

    // 🚀 Cancel any pending requests
    if (this.currentRequestController) {
      this.currentRequestController.abort();
    }

    // Abort any pending requests
    this.abortController.abort();

    // 🚀 Clear cache
    this.suggestionCache.clear();
  }

  // 🚀 Helper method to detect desktop mode
  isDesktopMode = () => {
    return window.innerWidth >= 992; // Bootstrap lg breakpoint
  };

  handleClickOutside = (event) => {
    if (
      this.searchContainerRef.current &&
      !this.searchContainerRef.current.contains(event.target) &&
      this.suggestionContainerRef.current &&
      !this.suggestionContainerRef.current.contains(event.target)
    ) {
      this.setState({ showSuggestions: false });
    }
  };

  debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      const context = this;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(context, args), delay);
    };
  };

  // Helper method to safely update state only if component is mounted
  safeSetState = (stateUpdate) => {
    if (this._isMounted) {
      this.setState(stateUpdate);
    }
  };

  initializeSearchHistoryApi = async () => {
    try {
      if (searchHistoryApi?.testConnection) {
        await searchHistoryApi.testConnection();
        this.safeSetState((prevState) => ({
          ...prevState,
          isTrackingEnabled: true,
          isInitialized: true,
        }));
      } else {
        this.safeSetState((prevState) => ({
          ...prevState,
          isTrackingEnabled: false,
          isInitialized: true,
        }));
      }
    } catch (error) {
      // Only update state if component is still mounted and request wasn't aborted
      if (this._isMounted && !this.abortController.signal.aborted) {
        this.safeSetState((prevState) => ({
          ...prevState,
          isTrackingEnabled: false,
          isInitialized: true,
        }));
      }
    }
  };

  buildSearchQuery = (query, isExactMatch = false) => {
    if (!query || !query.trim()) return "";

    const trimmedQuery = query.trim();

    if (isExactMatch) {
      const escapedQuery = `"${trimmedQuery}"`;
      return `(title:${escapedQuery} OR authors.full_name:${escapedQuery} OR abstract:${escapedQuery})`;
    }

    if (trimmedQuery.includes(":")) {
      return trimmedQuery;
    }

    const wildcardQuery = `${trimmedQuery}*`;
    return `(title:${wildcardQuery}^3 OR authors.full_name:${wildcardQuery}^2 OR abstract:${wildcardQuery} OR keywords.value:${wildcardQuery})`;
  };

  fetchSuggestions = async (query) => {
    if (!query || query.length < 2) {
      this.safeSetState({
        suggestions: [],
        showSuggestions: false,
        suggestionMetadata: new Map(),
      });
      return;
    }

    if (!this.state.isTrackingEnabled) {
      this.safeSetState({
        suggestions: [],
        showSuggestions: false,
        suggestionMetadata: new Map(),
      });
      return;
    }

    // 🚀 Check cache first
    const cacheKey = query.toLowerCase();
    const cachedData = this.suggestionCache.get(cacheKey);
    const now = Date.now();

    if (cachedData && now - cachedData.timestamp < this.cacheTimeout) {
      this.safeSetState({
        suggestions: cachedData.suggestions,
        suggestionMetadata: cachedData.metadata,
        showSuggestions: true,
        isLoadingSuggestions: false,
      });
      return;
    }

    // 🚀 Cancel previous request
    if (this.currentRequestController) {
      this.currentRequestController.abort();
    }

    // 🚀 Create new abort controller
    this.currentRequestController = new AbortController();

    this.safeSetState({ isLoadingSuggestions: true });

    try {
      // 🚀 Optimized search query with limit
      const searchQuery = `(title:${query}* OR authors.full_name:${query}*)`;
      const response = await literatureApi.list(searchQuery, {
        signal: this.currentRequestController.signal,
        size: 5, // 🚀 Limit results from API
      });

      const hits = response.data.hits || [];
      const suggestionSet = new Set();
      const metadataMap = new Map();

      // 🚀 Process only what we need
      hits.slice(0, 5).forEach((item) => {
        const title = item.metadata?.title;
        const authors = item.metadata?.authors
          ?.slice(0, 3) // 🚀 Limit authors to first 3
          .map((a) => a.full_name)
          .join(", ");
        const year = item.metadata?.publication_year;
        const pid = item.metadata?.pid;
        const coverUrl = item.metadata?.cover_metadata?.urls?.small;

        if (title || authors) {
          const uniqueKey = `${title || ""}${authors || ""}${year || ""}`;
          const label = {
            title: title || "Untitled",
            authors: authors || "Unknown Author",
            year: year || "N/A",
            pid,
            coverUrl,
            originalItem: item,
          };
          suggestionSet.add(uniqueKey);
          metadataMap.set(uniqueKey, label);
        }
      });

      const suggestions = Array.from(suggestionSet);

      // 🚀 Cache the results
      this.suggestionCache.set(cacheKey, {
        suggestions,
        metadata: metadataMap,
        timestamp: now,
      });

      this.safeSetState({
        suggestions,
        showSuggestions: true,
        isLoadingSuggestions: false,
        suggestionMetadata: metadataMap,
      });
    } catch (error) {
      // 🚀 Don't show error if request was aborted
      if (error.name === "AbortError") return;

      this.safeSetState({
        suggestions: [],
        isLoadingSuggestions: false,
        showSuggestions: true,
        suggestionMetadata: new Map(),
      });
    } finally {
      this.currentRequestController = null;
    }
  };

  handleInputChange = (value) => {
    this.safeSetState({ currentQuery: value });

    // 🚀 Clear suggestions immediately for empty input
    if (!value || value.length < 2) {
      this.safeSetState({
        suggestions: [],
        showSuggestions: false,
        suggestionMetadata: new Map(),
      });
      return;
    }

    this.debouncedFetchSuggestions(value);
  };

  handleSuggestionSelect = (suggestion) => {
    this.safeSetState({
      currentQuery: suggestion,
      showSuggestions: false,
    });
    this.onSearchExecute(suggestion, true);
  };

  handleInputFocus = () => {
    const { currentQuery, suggestions } = this.state;
    if (currentQuery.length > 1 && suggestions.length > 0) {
      this.safeSetState({ showSuggestions: true });
    }
  };

  handleInputBlur = () => {
    setTimeout(() => {
      this.safeSetState({ showSuggestions: false });
    }, 150);
  };

  loadSearchHistory = async (params = {}) => {
    if (!this.state.isTrackingEnabled || !this._isMounted) return;

    this.safeSetState({ isLoadingHistory: true, error: null });

    try {
      const requestParams = { ...DEFAULT_SEARCH_HISTORY_PARAMS, ...params };
      const response = await searchHistoryApi.list(requestParams);
      const items = response.data?.hits || [];

      this.safeSetState({
        searchHistory: items,
        isLoadingHistory: false,
      });
    } catch (error) {
      // Only update state if component is still mounted and request wasn't aborted
      if (this._isMounted && error.name !== "AbortError") {
        this.safeSetState({
          searchHistory: [],
          isLoadingHistory: false,
          error: error.message || "Failed to load search history",
        });
      }
    }
  };

  loadSearchStats = async (params = {}) => {
    if (!this.state.isTrackingEnabled || !this._isMounted) return;

    this.safeSetState({ isLoadingStats: true });

    try {
      const requestParams = { ...DEFAULT_SEARCH_STATS_PARAMS, ...params };
      const response = await searchHistoryApi.stats(requestParams);

      this.safeSetState({
        searchStats: response.data,
        isLoadingStats: false,
      });
    } catch (error) {
      // Only update state if component is still mounted and request wasn't aborted
      if (this._isMounted && error.name !== "AbortError") {
        this.safeSetState({
          searchStats: null,
          isLoadingStats: false,
        });
      }
    }
  };

  refreshData = () => {
    if (!this._isMounted) return;
    this.loadSearchHistory();
    this.loadSearchStats();
  };

  trackSearchExecution = async (query, searchStartTime) => {
    const { isTrackingEnabled } = this.state;
    if (!isTrackingEnabled || !searchStartTime || !this._isMounted) return;

    const searchMetadata = {
      total: null,
      hits: [],
      error: null,
      navigatedToResults: true,
    };
    const filterMetadata = {
      filters: {},
      sort: null,
      scope: "home_search",
    };
    await searchHistoryApi.trackSearchPerformance(
      query,
      searchStartTime,
      searchMetadata,
      filterMetadata
    );
  };

  onSearchExecute = async (query, isExactMatch = false) => {
    const searchQuery = query || this.state.currentQuery;
    const trimmedQuery = searchQuery?.trim();
    if (!trimmedQuery) return;

    const encodedQuery = encodeURIComponent(trimmedQuery);

    const searchStartTime = this.state.isTrackingEnabled
      ? new Date().getTime()
      : null;

    goTo(FrontSiteRoutes.documentsListWithQuery(encodedQuery));

    if (this.state.isTrackingEnabled && searchStartTime) {
      setTimeout(() => {
        this.trackSearchExecution(trimmedQuery, searchStartTime);
      }, 0);
    }
  };

  handleRepeatSearch = async (query) => {
    if (!this._isMounted) return;
    this.safeSetState({ showSearchHistory: false });
    const encodedQuery = encodeURIComponent(query);
    goTo(FrontSiteRoutes.documentsListWithQuery(encodedQuery));
  };

  handleDeleteSearchEntry = async (entryId) => {
    if (!this.state.isTrackingEnabled || !this._isMounted) return;

    await searchHistoryApi.delete(entryId);
    await this.refreshData();
  };

  handleClearSearchHistory = async () => {
    if (!this.state.isTrackingEnabled || !this._isMounted) return;

    await searchHistoryApi.clearHistory();
    await this.refreshData();
  };

  handleSearchInHistory = async (searchTerm, params = {}) => {
    if (!this.state.isTrackingEnabled || !this._isMounted) return [];

    try {
      const response = await searchHistoryApi.searchInHistory(
        searchTerm,
        params
      );
      return response.data || [];
    } catch (error) {
      return [];
    }
  };

  toggleSearchHistory = () => {
    if (!this._isMounted) return;

    const newShowState = !this.state.showSearchHistory;
    this.safeSetState({
      showSearchHistory: newShowState,
      showSuggestions: false,
    });

    if (newShowState) {
      this.refreshData();
    }
  };

  // 🚀 Memoized suggestion renderer for better performance
  renderSuggestion = (key, index) => {
    const data = this.state.suggestionMetadata.get(key);

    return (
      <div
        key={index}
        onClick={() => this.handleSuggestionSelect(data.title)}
        onMouseDown={(e) => e.preventDefault()}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 16px",
          cursor: "pointer",
          borderBottom:
            index < this.state.suggestions.length - 1
              ? "1px solid rgba(34, 36, 38, 0.08)"
              : "none",
          transition: "background 0.1s ease", // 🚀 Faster transition
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#f9f9f9")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
      >
        {/* 🚀 Optimized image loading */}
        {data?.coverUrl ? (
          <img
            src={data.coverUrl}
            alt=""
            loading="lazy" // 🚀 Lazy loading
            style={{
              width: "40px",
              height: "60px",
              objectFit: "cover",
              borderRadius: "4px",
              marginRight: "10px",
            }}
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
        ) : null}

        {/* 🚀 Fallback placeholder */}
        <div
          style={{
            width: "40px",
            height: "60px",
            background: "#ddd",
            borderRadius: "4px",
            marginRight: "10px",
            display: data?.coverUrl ? "none" : "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            color: "#666",
            flexShrink: 0, // 🚀 Prevent shrinking
          }}
        >
          📚
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: "#333",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap", // 🚀 Prevent text wrapping
            }}
          >
            {data?.title}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#666",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {data?.authors}
          </div>
          <div style={{ fontSize: "12px", color: "#999" }}>{data?.year}</div>
        </div>
      </div>
    );
  };

  render() {
    const {
      isTrackingEnabled,
      isInitialized,
      showSearchHistory,
      searchHistory,
      searchStats,
      isLoadingHistory,
      isLoadingStats,
      error,
      suggestions,
      showSuggestions,
      currentQuery,
      isLoadingSuggestions,
    } = this.state;

    if (!isInitialized) {
      return null;
    }

    const isDesktop = this.isDesktopMode();

    return (
      <div
        ref={this.searchContainerRef}
        style={{
          position: "relative",
          width: isDesktop ? "200%" : "100%",
          maxWidth: isDesktop ? "1390px" : "100%",
          margin: "0 auto",
          marginLeft: isDesktop ? "-197px" : "0",
          padding: isDesktop ? "0" : "0 15px",
        }}
      >
        <div style={{ display: "flex", alignItems: "stretch", gap: "0.5em" }}>
          <div style={{ flex: 1, position: "relative" }}>
            <EnhancedSearchBarILS
              onPasteHandler={() => {}}
              onSearchHandler={this.onSearchExecute}
              placeholder={invenioConfig.APP.HOME_SEARCH_BAR_PLACEHOLDER}
              className="fs-headline"
              onChangeHandler={this.handleInputChange}
              value={currentQuery}
              onFocus={this.handleInputFocus}
              onBlur={this.handleInputBlur}
              onDeleteHistoryEntry={this.handleDeleteSearchEntry}
              onClearSearchHistory={this.handleClearSearchHistory}
              onRefreshHistory={this.refreshData}
              searchHistory={searchHistory}
              isLoadingHistory={isLoadingHistory}
              historyError={error}
            />
            {/* 🚀 Show suggestions dropdown when active with desktop left padding */}
            {showSuggestions &&
              ReactDOM.createPortal(
                <div
                  ref={this.suggestionContainerRef}
                  style={{
                    position: "absolute",
                    top:
                      (this.searchContainerRef.current?.getBoundingClientRect()
                        .bottom || 0) + window.scrollY,
                    left:
                      (this.searchContainerRef.current?.getBoundingClientRect()
                        .left || 0) +
                      window.scrollX +
                      (isDesktop ? 200 : 0), // 🚀 Add 200px left padding on desktop
                    width:
                      (this.searchContainerRef.current?.offsetWidth || 0) -
                      (isDesktop ? 200 : 0), // 🚀 Adjust width to account for padding
                    zIndex: 1,
                    backgroundColor: "white",
                    border: "1px solid rgba(34, 36, 38, 0.15)",
                    borderTop: "none",
                    borderRadius: "0 0 0.28571429rem 0.28571429rem",
                    boxShadow: "0 4px 12px 0 rgba(34, 36, 38, 0.15)",
                    maxHeight: "300px",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {isLoadingSuggestions ? (
                    <div
                      style={{
                        padding: "12px 16px",
                        textAlign: "center",
                        color: "#999",
                      }}
                    >
                      Loading...
                    </div>
                  ) : suggestions.length > 0 ? (
                    // 🚀 Use memoized renderer
                    suggestions.map(this.renderSuggestion)
                  ) : (
                    <div
                      style={{
                        padding: "12px 16px",
                        textAlign: "center",
                        color: "#999",
                      }}
                    >
                      No suggestions found
                    </div>
                  )}
                </div>,
                document.body
              )}
          </div>
          {isTrackingEnabled && (
            <Button
              icon
              onClick={this.toggleSearchHistory}
              title="View Search History"
              size="big"
              style={{
                padding: "0.70em 0.70em",
                borderRadius: "0.28571429rem",
              }}
            >
              <Icon name="history" />
            </Button>
          )}
        </div>
        {isTrackingEnabled && (
          <SearchHistoryModal
            open={showSearchHistory}
            onClose={() => this.safeSetState({ showSearchHistory: false })}
            onSearchExecute={this.handleRepeatSearch}
            onDeleteEntry={this.handleDeleteSearchEntry}
            onClearHistory={this.handleClearSearchHistory}
            onSearchInHistory={this.handleSearchInHistory}
            onRefresh={this.refreshData}
            searchHistory={searchHistory}
            searchStats={searchStats}
            isLoadingHistory={isLoadingHistory}
            isLoadingStats={isLoadingStats}
            error={error}
          />
        )}
      </div>
    );
  }
}
